/**
 * Consumer / freemail hosts — domain Wayback is noise for these mailboxes.
 * Corporate / custom domains keep Internet Archive cards.
 */
export const PUBLIC_EMAIL_PROVIDERS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'ymail.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'icloud.com',
  'me.com',
  'mac.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'aol.com',
  'gmx.com',
  'gmx.net',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'yandex.ru',
]);

/**
 * Extract domain from an email address.
 *
 * @param {unknown} email
 * @returns {string}
 */
export function emailDomainFromAddress(email) {
  const raw = String(email || '').trim().toLowerCase();
  const at = raw.lastIndexOf('@');
  if (at < 0) return '';
  return raw.slice(at + 1).replace(/\.$/, '');
}

/**
 * True when the mailbox domain is a known public / consumer provider.
 *
 * @param {unknown} emailOrDomain
 * @returns {boolean}
 */
export function isPublicEmailProvider(emailOrDomain) {
  const raw = String(emailOrDomain || '').trim().toLowerCase();
  if (!raw) return false;
  const domain = raw.includes('@') ? emailDomainFromAddress(raw) : raw.replace(/^@/, '');
  if (!domain) return false;
  if (PUBLIC_EMAIL_PROVIDERS.has(domain)) return true;
  // Catch regional yahoo.* / hotmail.* style consumer TLDs.
  if (/^(yahoo|hotmail|outlook|live|msn|icloud|gmail|googlemail|protonmail)\./i.test(domain)) {
    return true;
  }
  return false;
}

/**
 * Collapse identical EMAIL INFRASTRUCTURE / MX cards before render.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function dedupeEmailInfrastructureRows(rows) {
  const byKey = new Map();
  for (const row of rows || []) {
    if (!row || typeof row !== 'object') continue;
    const details = row.details && typeof row.details === 'object' ? row.details : {};
    const host = String(details.hostname || details.email_domain || details.domain || row.title || '')
      .trim()
      .toLowerCase();
    const role = String(details.email_infra_role || details.corp_infra_role || 'domain')
      .trim()
      .toLowerCase();
    const mx =
      details.infra_inspect?.dns?.MX ||
      details.dns?.MX ||
      details.mx_hosts ||
      details.mx ||
      null;
    const mxSig = Array.isArray(mx)
      ? mx
          .map((m) =>
            typeof m === 'string'
              ? m.toLowerCase()
              : `${m?.preference ?? ''}:${String(m?.host || m?.exchange || '').toLowerCase()}`,
          )
          .sort()
          .join('|')
      : String(mx || '');
    const key = `${host}|${role}|${mxSig}|${String(row.url || '').toLowerCase()}`;
    if (!byKey.has(key)) {
      byKey.set(key, row);
    }
  }
  return [...byKey.values()];
}

/**
 * Merge domain + MX host infrastructure cards into one Email Server & Routing block.
 * Corporate variant callers should skip this and keep per-host cards.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function mergeEmailServerRoutingRows(rows) {
  const list = dedupeEmailInfrastructureRows(rows);
  if (list.length <= 1) {
    return list.map((row) => ({
      ...row,
      details: {
        ...(row.details || {}),
        email_server_routing: true,
        routing_hosts: [
          String(row.details?.hostname || row.details?.email_domain || row.details?.domain || '').trim(),
        ].filter(Boolean),
      },
    }));
  }

  const hosts = [];
  const seen = new Set();
  let primary = null;
  for (const row of list) {
    const role = String(row.details?.email_infra_role || row.details?.corp_infra_role || 'domain')
      .trim()
      .toLowerCase();
    if (!primary || role === 'domain') primary = row;
    const host = String(
      row.details?.hostname || row.details?.email_domain || row.details?.domain || '',
    ).trim();
    if (host && !seen.has(host.toLowerCase())) {
      seen.add(host.toLowerCase());
      hosts.push({
        host,
        role: role === 'mx_host' ? 'MX' : 'Domain',
        row,
      });
    }
  }

  const base = primary || list[0];
  const emailDomain = String(
    base.details?.email_domain || base.details?.domain || hosts[0]?.host || '',
  ).trim();

  return [
    {
      ...base,
      title: emailDomain
        ? `Email Server & Routing — ${emailDomain}`
        : 'Email Server & Routing',
      details: {
        ...(base.details || {}),
        email_infra_role: 'routing',
        email_server_routing: true,
        email_domain: emailDomain,
        hostname: emailDomain || base.details?.hostname,
        routing_hosts: hosts,
        host_count: hosts.length,
        total_services: list.reduce(
          (acc, r) => acc + (Number(r.details?.total_services) || 0),
          0,
        ),
        // Keep primary inspect payload; secondary hosts listed in UI chips.
        merged_infra_rows: list,
      },
    },
  ];
}
